import { moduleLogger } from "../Logger";
import type { DetailviewBenefit } from "../benefit/DetailviewBenefit";

const log = moduleLogger.scope("activation-slider");

export class Slider {
	private static readonly SLIDER_ID = "benefit_partner_discount_activate";
	private static readonly SLIDER_LABEL_ID = "benefit_partner_discount_activate_label";
	private static readonly SLIDER_LOADER_ID = "benefit_partner_discount_activate_loader";

	private readonly sliderIdSuffix: string;
	constructor(sliderIdSuffix: string) {
		this.sliderIdSuffix = sliderIdSuffix;
	}

	private loaderTimeout?: NodeJS.Timeout = undefined;

	init(callback: () => void, targetElement: HTMLElement) {
		const slider = targetElement.querySelector(`#${Slider.SLIDER_ID}${this.sliderIdSuffix}`);
		const sliderLabel = targetElement.querySelector(`#${Slider.SLIDER_LABEL_ID}${this.sliderIdSuffix}`);
		if (slider && slider instanceof HTMLInputElement && sliderLabel && sliderLabel instanceof HTMLLabelElement) {
			this.createClickListener(slider, callback, targetElement);
			this.createTouchListener(slider, sliderLabel);
		}
	}

	getSliderHtml(isChecked: boolean, detailviewBenefit: DetailviewBenefit, withAnimation = false): string {
		const checkmarkClass = withAnimation ? "activation_slider-checkmark-with-animation" : "";
		if (isChecked) {
			return `<div class="activation_slider-checkmark-container">
                        <svg class="activation_slider-checkmark-wrapper" xmlns="http:/*                                       */
                              <circle class="activation_slider-checkmark-circle" cx="26" cy="26" r="27"/>
                              <path class="activation_slider-checkmark-check ${checkmarkClass}" fill="none" d="M15.3 27.2l7.1 7.2 16.7 -16.8"/>
                        </svg>
                        <nobr class="pl_ml50 pl_headline25">
                        ${detailviewBenefit.activatedSliderText}
                        </nobr>
                    </div>`;
		}
		return `<div class="activation_slider-slider-container">
                        <input class="activation_slider-slider-input" type="checkbox" id="${Slider.SLIDER_ID}${this.sliderIdSuffix}" />
                        <label class="activation_slider-slider-label pl_headline25" for="${Slider.SLIDER_ID}${this.sliderIdSuffix}" id="${Slider.SLIDER_LABEL_ID}${this.sliderIdSuffix}">${detailviewBenefit.deactivatedSliderText}</label>
                        <div class="p_loader100 activation_slider-slider-loader" id="${Slider.SLIDER_LOADER_ID}${this.sliderIdSuffix}" style="visibility: hidden;"></div>
                    </div>`;
	}

	createClickListener(slider: HTMLInputElement, callback: () => void, targetElement: HTMLElement) {
		slider.addEventListener("change", () => {
			slider.disabled = true;

			const sliderLabel = targetElement.querySelector(`#${Slider.SLIDER_LABEL_ID}${this.sliderIdSuffix}`);
			if (sliderLabel && sliderLabel instanceof HTMLLabelElement) {
				sliderLabel.style.color = "rgba(34, 34, 34, 0)";
			}

			this.loaderTimeout = setTimeout(() => {
				/*                                                               */
				const loader = targetElement.querySelector(`#${Slider.SLIDER_LOADER_ID}${this.sliderIdSuffix}`);
				if (loader && loader instanceof HTMLDivElement) {
					loader.style.visibility = "";
				}
			}, 300);

			callback();
		});
	}

	createTouchListener(slider: HTMLInputElement, sliderLabel: HTMLLabelElement) {
		const sliderWidth = sliderLabel.offsetWidth;

		const maxMovedBy =
			sliderLabel.offsetWidth -
			10 - /*                                    */
			24; /*                       */

		const startColor = "#C4C4C4";
		const endColor = "#007268";

		log.info(`[benefit-detailview] Register touch slider for manual activation: sliderWidth: ${sliderWidth}, ` + `minMovedBy: 0, maxMovedBy: ${maxMovedBy}`);

		sliderLabel.addEventListener("touchstart", startTouch);
		sliderLabel.addEventListener("touchend", endTouch);
		sliderLabel.addEventListener("touchmove", moveTouch);

		sliderLabel.addEventListener("mousedown", startTouch);
		sliderLabel.addEventListener("mouseup", endTouch);
		sliderLabel.addEventListener("mouseleave", endTouch);
		sliderLabel.addEventListener("mousemove", moveTouch);

		let isDragging = false;
		let startPos = 0;
		let movedBy = 0;
		const stylingElement: HTMLStyleElement = sliderLabel.appendChild(document.createElement("style"));

		function startTouch(event: TouchEvent | MouseEvent) {
			const currentPosition = Slider.getPositionX(event);
			const elementPosition = sliderLabel.getBoundingClientRect().left;
			const minPosition = elementPosition;
			const maxPosition = 44 + elementPosition;

			if (!slider.disabled && currentPosition !== undefined && currentPosition >= minPosition && currentPosition <= maxPosition) {
				log.info(`[benefit-detailview] Touch start: currentPosition: ${currentPosition}`);

				startPos = currentPosition;
				movedBy = 0;
				isDragging = true;
			} else {
				log.info(`[benefit-detailview] Touch start, but out of area or disabled.. slider.disabled: ${String(slider.disabled)}, currentPosition: ${currentPosition}, minPosition: ${minPosition}, maxPosition: ${maxPosition}`);
			}
		}

		function endTouch() {
			if (!isDragging) {
				return;
			}

			const movedByPercent = movedBy / maxMovedBy;
			log.info(`[benefit-detailview] Touch end: movedByPercent: ${movedByPercent}`);
			if (movedByPercent >= 0.5) {
				log.info("[benefit-detailview] movedByPercent is higher than threshold. Perform click!");
				slider.click();
			}

			isDragging = false;
			stylingElement.innerHTML = "";
			sliderLabel.style.background = "";
		}

		function moveTouch(event: TouchEvent | MouseEvent) {
			if (!isDragging) {
				return;
			}

			const currentPosition = Slider.getPositionX(event);
			if (currentPosition === undefined) {
				return;
			}

			movedBy = Math.max(Math.min(currentPosition - startPos, maxMovedBy), 0);

			const movedByPercent = movedBy / maxMovedBy;
			const newColor = Slider.getColorByPercent(startColor, endColor, movedByPercent);
			sliderLabel.style.background = newColor;
			sliderLabel.style.color = `rgba(34, 34, 34, ${1 - movedByPercent})`;

			log.info(`[benefit-detailview] Moved: movedBy: ${movedBy}, movedByPercent: ${movedByPercent}, newColor: ${newColor}`);

			stylingElement.innerHTML = `#${sliderLabel.id}:after {transform: translate(${movedBy}px); transition: unset}`;
		}
	}

	static getPositionX(event: TouchEvent | MouseEvent) {
		return event.type.includes("mouse") ? (event as MouseEvent).pageX : (event as TouchEvent).touches[0]?.clientX;
	}

	static getColorByPercent(startColor: string, endColor: string, movedByPercent: number): string {
		if (movedByPercent > 1) {
			return endColor;
		}
		if (movedByPercent < 0) {
			return startColor;
		}

		function hexToRgb(hexString: string) {
			/*                                                              */
			const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
			const replacedHexString = hexString.replace(shorthandRegex, (m, r: string, g: string, b: string): string => r + r + g + g + b + b);

			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(replacedHexString);
			if (result?.[1] && result[2] && result[3]) {
				return {
					r: Number.parseInt(result[1], 16),
					g: Number.parseInt(result[2], 16),
					b: Number.parseInt(result[3], 16),
				};
			}
			return { r: 0, g: 0, b: 0 };
		}

		function rgbToHex(r: number, g: number, b: number): string {
			return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
		}

		const rgbStartColor = hexToRgb(startColor);
		const rgbEndColor = hexToRgb(endColor);

		const w2 = 1 - movedByPercent;
		return rgbToHex(Math.round(rgbEndColor.r * movedByPercent + rgbStartColor.r * w2), Math.round(rgbEndColor.g * movedByPercent + rgbStartColor.g * w2), Math.round(rgbEndColor.b * movedByPercent + rgbStartColor.b * w2));
	}

	reset(targetElement: HTMLElement) {
		clearTimeout(this.loaderTimeout);

		const slider = targetElement.querySelector(`#${Slider.SLIDER_ID}${this.sliderIdSuffix}`);
		if (slider && slider instanceof HTMLInputElement) {
			slider.disabled = false;
			slider.checked = false;
		}
		const sliderLoader = targetElement.querySelector(`#${Slider.SLIDER_LOADER_ID}${this.sliderIdSuffix}`);
		if (sliderLoader && sliderLoader instanceof HTMLDivElement) {
			sliderLoader.style.visibility = "hidden";
		}

		const sliderLabel = targetElement.querySelector(`#${Slider.SLIDER_LABEL_ID}${this.sliderIdSuffix}`);
		if (sliderLabel && sliderLabel instanceof HTMLLabelElement) {
			sliderLabel.style.color = "rgba(34, 34, 34, 100)";
		}
	}
}
